import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { BarChart3, Trophy, AlertTriangle } from 'lucide-react';
import SeasonSwitcher from '../components/SeasonSwitcher';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const INDICATORS = [
  { key: 'form_index', label: 'Form' },
  { key: 'availability_score', label: 'Availability' },
  { key: 'development_score', label: 'Development' },
  { key: 'consistency_score', label: 'Consistency' },
];

const DEFAULT_SQUADS = ['Senior', 'U19', 'U16', 'U14', 'U12'];

const fmt = (v) =>
  v === null || v === undefined || Number.isNaN(Number(v)) ? '—' : Math.round(Number(v));

const pct = (n, total) => {
  if (!total) return '—';
  return `${Math.round((n / total) * 100)}%`;
};

const SquadAnalytics = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'Admin';
  const [squads, setSquads] = useState([]);
  const [selected, setSelected] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [seasonId, setSeasonId] = useState('');

  // Discover squads available to the current user.
  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    axios
      .get(`${API}/api/athletes?status=active`, h)
      .then((r) => {
        const list = r.data || [];
        const distinct = Array.from(
          new Set(list.map((a) => a.squad).filter((s) => s && s.trim()))
        );
        // Admin sees all known squads; Staff only sees squads derived from assigned athletes.
        const squadList = isAdmin
          ? Array.from(new Set([...DEFAULT_SQUADS, ...distinct]))
          : distinct;
        setSquads(squadList);
        if (squadList.length && !selected) setSelected(squadList[0]);
      })
      .catch(() => toast.error('Failed to load squads'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  useEffect(() => {
    if (!selected) return;
    const h = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    setLoading(true);
    const params = seasonId ? `?season_id=${encodeURIComponent(seasonId)}` : '';
    axios
      .get(`${API}/api/analytics/squad/${encodeURIComponent(selected)}/overview${params}`, h)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load squad analytics'))
      .finally(() => setLoading(false));
  }, [selected, seasonId]);

  const hasAthletes = (data?.active_athletes || 0) > 0;

  return (
    <Layout>
      <div className="space-y-4" data-testid="squad-analytics-page">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <BarChart3 className="text-emerald-600" size={22} />
              Squad Analytics
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Aggregate indicators across every active athlete in the squad.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <SeasonSwitcher value={seasonId} onChange={setSeasonId} userRole={user?.role} testId="squad-season-switcher" />
            <div className="w-52">
              <Select value={selected} onValueChange={setSelected}>
              <SelectTrigger data-testid="squad-select-trigger">
                <SelectValue placeholder="Select squad" />
              </SelectTrigger>
              <SelectContent>
                {squads.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`squad-option-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-gray-500">Loading…</div>
        ) : squads.length === 0 ? (
          <Card data-testid="squad-no-access">
            <CardContent className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
              No squads assigned. Ask an admin to link athletes to your account to view squad analytics.
            </CardContent>
          </Card>
        ) : !data ? (
          <Card data-testid="squad-no-data">
            <CardContent className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
              Select a squad to view analytics.
            </CardContent>
          </Card>
        ) : !hasAthletes ? (
          <Card data-testid="squad-empty-state">
            <CardContent className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
              No active athletes in this squad
            </CardContent>
          </Card>
        ) : (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="squad-kpi-row">
              {INDICATORS.map(({ key, label }) => {
                const avg = data.averages?.[key];
                const med = data.medians?.[key];
                const cov = data.coverage?.[key] ?? 0;
                return (
                  <Card key={key} data-testid={`squad-kpi-${key}`}>
                    <CardContent className="pt-4 pb-3">
                      <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        {label}
                      </p>
                      <p className="text-3xl font-semibold text-gray-900 dark:text-gray-100 mt-1 tabular-nums">
                        {fmt(avg)}
                      </p>
                      <div className="flex justify-between text-[10px] text-gray-400 dark:text-gray-500 mt-2">
                        <span>median {fmt(med)}</span>
                        <span>coverage {pct(cov, data.active_athletes)}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Leaderboards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="squad-leaderboards">
              <LeaderList
                title="Top Performers"
                icon={Trophy}
                iconColor="text-emerald-600"
                entries={data.leaderboards?.form_index?.top || []}
                testId="squad-leaderboard-top"
                onClick={(id) => navigate(`/athlete/${id}/360`)}
              />
              <LeaderList
                title="Needs Attention"
                icon={AlertTriangle}
                iconColor="text-amber-600"
                entries={data.leaderboards?.form_index?.bottom || []}
                testId="squad-leaderboard-bottom"
                onClick={(id) => navigate(`/athlete/${id}/360`)}
              />
            </div>
            <p className="text-[11px] text-gray-400 dark:text-gray-500">
              {data.active_athletes} active athletes in {data.squad}
            </p>
          </>
        )}
      </div>
    </Layout>
  );
};

const LeaderList = ({ title, icon: Icon, iconColor, entries, testId, onClick }) => (
  <Card data-testid={testId}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
        <Icon size={14} className={iconColor} />
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      {entries.length === 0 ? (
        <p className="py-6 text-center text-xs text-gray-400">
          No ratings yet — evaluations will appear after the next match.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {entries.map((e, i) => (
            <li
              key={e.athlete_id}
              className="flex items-center justify-between py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 -mx-3 px-3 rounded"
              onClick={() => onClick(e.athlete_id)}
              data-testid={`${testId}-row-${e.athlete_id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[11px] text-gray-400 w-5 tabular-nums">#{i + 1}</span>
                <span className="text-xs text-gray-800 dark:text-gray-200 truncate">
                  {e.athlete_name}
                </span>
                {e.position ? (
                  <Badge className="text-[9px] bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                    {e.position}
                  </Badge>
                ) : null}
              </div>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 tabular-nums ml-2">
                {fmt(e.value)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </CardContent>
  </Card>
);

export default SquadAnalytics;
